/**
 * Effective status — what a vehicle and a soldier *actually* are right now,
 * derived from the board rather than read off their own record.
 *
 * The rosters store an operator's intent: a vehicle is `available`, a soldier
 * is `available`, and somebody has to remember to change that when the convoy
 * rolls. Nobody ever does, so the two screens drifted from the board within an
 * hour of a busy morning.
 *
 * This module removes the bookkeeping. A vehicle bound to a team on an active
 * task (בביצוע) reads as **במשימה**; a soldier riding one — driver, escort or
 * passenger — reads as **במשימה** too, on every screen, and falls back to
 * standing the moment the task is closed. Nothing is written: the stored record
 * is left exactly as the operator set it, and the derivation is applied on the
 * way to the screen.
 *
 * One rule decides the precedence, and it is the same for both: **the
 * assignment upgrades a record that says "standing", and never overrules one
 * that says "cannot go".** A vehicle in the workshop stays בטיפול and a soldier
 * on leave stays בחופשה even while a manager has overridden the conflict and
 * sent them anyway — that override is a finding the ledger already carries, and
 * hiding it behind a green "במשימה" would be the wrong way round.
 *
 * Everything here is pure: no Dexie, no React and no clock of its own. The
 * callers hand in the lists they already loaded.
 */
import { teamSoldierIds } from './assignment-rules';
import { isDeleted } from './recycle-bin';
import {
  AVAILABILITY_ICONS,
  TIMED_STATUS_ICONS,
  effectiveAvailability,
  formatTimedStatus,
  isTimedStatusActive,
} from './soldier-status';
import { SOLDIER_AVAILABILITY_LABELS, VEHICLE_STATUS_LABELS } from '../types/fleet';
import { normalizeTaskStatus, ACTIVE_TASK_STATUS_ID } from '../types/task';
import type { Task } from '../types/task';
import type { TaskTeam } from '../types/assignment';
import type {
  Soldier,
  SoldierAvailability,
  TimedStatus,
  Vehicle,
  VehicleStatus,
} from '../types/fleet';

// ── The index ────────────────────────────────────────────────────────

/**
 * Who is out right now — the answer every screen asks for, computed once.
 *
 * Held as plain arrays rather than sets so the whole thing stays structurally
 * comparable: a `useMemo` on the tasks and the teams produces the same index
 * twice in a row when nothing moved, and React stops re-rendering four screens
 * over a convoy that did not change.
 */
export interface AssignmentIndex {
  /** Ids of the tasks that are running, i.e. בביצוע and not in the bin. */
  taskIds: readonly string[];
  /** Vehicles bound to a team on one of them. */
  vehicleIds: readonly string[];
  /** Roster soldiers riding one of them, in any crew role. */
  soldierIds: readonly string[];
}

export const EMPTY_ASSIGNMENT_INDEX: AssignmentIndex = {
  taskIds: [],
  vehicleIds: [],
  soldierIds: [],
};

/**
 * The tasks that are actually running: status בביצוע, and not sitting in the
 * recycle bin — a binned task holds nobody, whatever status it was thrown away
 * with. A day key narrows it to that day's board; without one, every active
 * task counts, which is what the rosters want (a convoy that left yesterday and
 * has not been closed is still out).
 */
export function activeTasksOf(tasks: readonly Task[], dateKey?: string): Task[] {
  return tasks.filter((task) => {
    if (isDeleted(task)) return false;
    if (normalizeTaskStatus(task.statusId) !== ACTIVE_TASK_STATUS_ID) return false;
    return !dateKey || task.date === dateKey;
  });
}

/** Builds the index out of the board and its convoys. */
export function activeAssignmentIndex(
  teams: readonly TaskTeam[],
  tasks: readonly Task[],
  dateKey?: string,
): AssignmentIndex {
  const taskIds = activeTasksOf(tasks, dateKey).map((task) => task.id);
  const active = new Set(taskIds);
  const onDuty = teams.filter((team) => active.has(team.taskId));
  return {
    taskIds,
    vehicleIds: [...new Set(onDuty.map((team) => team.vehicleId).filter(Boolean))],
    soldierIds: [...new Set(onDuty.flatMap(teamSoldierIds).filter(Boolean))],
  };
}

/** True when the vehicle is bound to a team on an active task. */
export function isVehicleOnActiveTask(
  vehicleId: string,
  index: AssignmentIndex = EMPTY_ASSIGNMENT_INDEX,
): boolean {
  return Boolean(vehicleId) && index.vehicleIds.includes(vehicleId);
}

/** True when the soldier rides an active task, in any crew role. */
export function isSoldierOnActiveTask(
  soldierId: string,
  index: AssignmentIndex = EMPTY_ASSIGNMENT_INDEX,
): boolean {
  return Boolean(soldierId) && index.soldierIds.includes(soldierId);
}

// ── Vehicles ─────────────────────────────────────────────────────────

/**
 * The status a vehicle is actually in. `available` becomes `in_use` while the
 * vehicle is out; בטיפול and מושבת are left alone — see the precedence rule at
 * the top of the file.
 */
export function effectiveVehicleStatus(
  vehicle: Vehicle,
  index: AssignmentIndex = EMPTY_ASSIGNMENT_INDEX,
): VehicleStatus {
  if (vehicle.status === 'available' && isVehicleOnActiveTask(vehicle.id, index)) return 'in_use';
  return vehicle.status;
}

/** One line for the vehicle card — the effective status, in Hebrew. */
export function describeVehicleStatus(
  vehicle: Vehicle,
  index: AssignmentIndex = EMPTY_ASSIGNMENT_INDEX,
): string {
  return VEHICLE_STATUS_LABELS[effectiveVehicleStatus(vehicle, index)];
}

// ── Soldiers ─────────────────────────────────────────────────────────

/**
 * The availability a soldier is actually in — their timed status first (that is
 * `effectiveAvailability`'s job), then the convoy on top of it. Only a soldier
 * who would otherwise read as standing is moved to במשימה.
 */
export function effectiveSoldierAvailability(
  soldier: Soldier,
  index: AssignmentIndex = EMPTY_ASSIGNMENT_INDEX,
  now: number = Date.now(),
): SoldierAvailability {
  const state = effectiveAvailability(soldier, now);
  if (state === 'available' && isSoldierOnActiveTask(soldier.id, index)) return 'on_mission';
  return state;
}

/**
 * The sentence the roster row carries. A running timed status still spells
 * itself out ("בבית עד יום שלישי 08:00"); a soldier out on a convoy reads
 * "במשימה" without one having been set by hand.
 */
export function describeEffectiveSoldierStatus(
  soldier: Soldier,
  index: AssignmentIndex = EMPTY_ASSIGNMENT_INDEX,
  now: number = Date.now(),
): string {
  // The wording of a timed status has one owner — `soldier-status` — and this
  // is only ever the branch that chooses between it and the plain label.
  const timed = formatTimedStatus(soldier.temporaryStatus, now);
  if (timed) return timed;
  return SOLDIER_AVAILABILITY_LABELS[effectiveSoldierAvailability(soldier, index, now)];
}

/** The glyph that sentence leads with. */
export function effectiveSoldierStatusIcon(
  soldier: Soldier,
  index: AssignmentIndex = EMPTY_ASSIGNMENT_INDEX,
  now: number = Date.now(),
): string {
  if (isTimedStatusActive(soldier.temporaryStatus, now)) {
    return TIMED_STATUS_ICONS[(soldier.temporaryStatus as TimedStatus).kind];
  }
  return AVAILABILITY_ICONS[effectiveSoldierAvailability(soldier, index, now)];
}
